import { forwardRef, useImperativeHandle, useState } from 'react'
import { azimuthDeg, type GameCalibration } from '@/lib/gameMap'
import { formatDistance, metersPerWorld, worldToGame } from '@/lib/mapScale'
import { WORLD_GROUP_STYLE, pinAt } from '@/lib/liveViewport'

/**
 * Cetvel — iki nokta arasındaki mesafe ve pusula açısı.
 *
 * Ölçüm kişiseldir: tuvale yazılmaz, kaydedilmez, WS'te yayılmaz. Salt okunur
 * bakan da ölçebilir. Sürükleme her karede yeni bir uç üretir; editörün tamamı
 * (~4.5k satır) bunun için yeniden çizilmesin diye ölçüm bu katmanın kendi
 * state'inde durur ve editör onu `set()` ile sürer.
 *
 * Zoom/pan'i CSS değişkenlerinden izler (bkz. `lib/liveViewport.ts`): çizgi
 * dünya koordinatında, uçlar ve yazı iğne. Zoom sırasında render olmaz.
 */

export interface RulerHandle {
  set: (m: { a: { x: number; y: number }; b: { x: number; y: number } } | null) => void
}

interface Props {
  worldSize: number
  cal: GameCalibration
  /** Cetvel aracı seçiliyken ölçüm yoksa ipucu basılır. */
  active: boolean
  hint: string
}

// Beyaz: sarı hotzone'un rengi, cetvel onunla karışmasın.
const COLOR = '#ffffff'

const RulerLayer = forwardRef<RulerHandle, Props>(function RulerLayer(
  { worldSize, cal, active, hint }, ref,
) {
  const [m, setM] = useState<{ a: { x: number; y: number }; b: { x: number; y: number } } | null>(null)
  useImperativeHandle(ref, () => ({ set: setM }), [])

  if (!m) {
    return active ? (
      <div className="pointer-events-none absolute bottom-4 left-1/2 z-20 -translate-x-1/2 rounded-sm border border-white/10 bg-black/75 px-3 py-1.5 text-xs text-white/80">
        {hint}
      </div>
    ) : null
  }

  const meters = Math.hypot(m.b.x - m.a.x, m.b.y - m.a.y) * metersPerWorld(worldSize)
  const az = azimuthDeg(worldToGame(m.a, cal, worldSize), worldToGame(m.b, cal, worldSize))
  const label = `${formatDistance(meters)} · ${Math.round(az) % 360}°`
  const mid = { x: (m.a.x + m.b.x) / 2, y: (m.a.y + m.b.y) / 2 }

  return (
    <div className="pointer-events-none absolute inset-0" style={{ zIndex: 21 }}>
      <svg className="absolute inset-0 h-full w-full" style={{ overflow: 'visible' }}>
        <g style={WORLD_GROUP_STYLE}>
          {/* Halo + renk: tek kat ince çizgi parlak tarlada kayboluyordu. */}
          <line x1={m.a.x} y1={m.a.y} x2={m.b.x} y2={m.b.y} stroke="rgba(0,0,0,0.75)" strokeWidth={4}
            strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          <line x1={m.a.x} y1={m.a.y} x2={m.b.x} y2={m.b.y} stroke={COLOR} strokeWidth={2}
            strokeDasharray="8 5" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        </g>
      </svg>
      {[m.a, m.b].map((p, i) => (
        <div key={i} className="absolute h-2 w-2 rounded-full border border-black"
          style={{ left: 0, top: 0, background: COLOR, transform: `${pinAt(p.x, p.y)} translate(-50%, -50%)` }} />
      ))}
      <span
        className="absolute whitespace-nowrap font-mono text-xs font-semibold text-white"
        style={{
          left: 0, top: 0,
          transform: `${pinAt(mid.x, mid.y)} translate(-50%, calc(-100% - 6px))`,
          textShadow: '0 0 3px #000, 0 0 2px #000, 0 1px 2px #000',
        }}
      >
        {label}
      </span>
    </div>
  )
})

export default RulerLayer
